import { useRef, useCallback, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import InitiativeCard from '../common/InitiativeCard'
import { focusAreas } from '../../data/partnerContent'
import { MOBILE_MQ, mobilePaddingX, mobileSectionTitleSx } from '../../theme/mobileLayout'
import { scale } from '../../theme/scale'
import { SLIDER_TRANSITION_SPEED } from '../../theme/slider'
import 'swiper/css'

const SLIDE_GAP = 40
const MOBILE_SLIDE_GAP = 20
const SLIDE_WIDTH = 440
const CARD_SHADOW_BLEED = 8

export default function PartnerFocusAreasSection() {
  const swiperRef = useRef(null)
  const slides = useMemo(() => [...focusAreas, ...focusAreas, ...focusAreas], [])

  const handleSwiper = useCallback((swiper) => {
    swiperRef.current = swiper
    requestAnimationFrame(() => swiper.autoplay?.start())
  }, [])

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        maxWidth: 1920,
        mx: 'auto',
        pl: mobilePaddingX,
        pr: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 5, md: '80px' },
        }}
      >
        <Box
          sx={{
            maxWidth: 1520,
            width: '100%',
            pr: mobilePaddingX,
            boxSizing: 'border-box',
          }}
        >
          <Typography
            component="h2"
            sx={{
              ...mobileSectionTitleSx,
              fontSize: { ...mobileSectionTitleSx.fontSize, lg: scale.fontSize.sectionTitle },
              lineHeight: 'normal',
              fontWeight: 400,
              color: '#111',
              [MOBILE_MQ]: { fontSize: 28 },
            }}
          >
            Our Focus Areas
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'none',
            [MOBILE_MQ]: { display: 'flex' },
            flexDirection: 'column',
            gap: '20px',
            pr: mobilePaddingX,
            boxSizing: 'border-box',
          }}
        >
          {focusAreas.map((item) => (
            <InitiativeCard
              key={item.title}
              {...item}
              sx={{
                minHeight: { xs: 300, md: 315 },
                '& .MuiTypography-root:first-of-type': {
                  fontSize: { xs: 24, sm: 24, md: 26, lg: 26 },
                },
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: 'block',
            [MOBILE_MQ]: { display: 'none' },
            width: {
              xs: 'calc(100vw - 40px)',
              sm: 'calc(100vw - 48px)',
              md: 'calc(100vw - 80px)',
              lg: 'calc(100vw - 160px)',
              xl: 'calc(100vw - 400px)',
            },
            maxWidth: 'none',
            position: 'relative',
            overflow: 'visible',
            '& .swiper': { overflow: 'visible', width: '100%' },
            '& .swiper-wrapper': { alignItems: 'stretch' },
            '& .swiper-slide': {
              width: {
                xs: 'calc(100vw - 40px) !important',
                sm: '360px !important',
                md: '400px !important',
                lg: `${SLIDE_WIDTH + CARD_SHADOW_BLEED * 2}px !important`,
              },
              height: 'auto',
              flexShrink: 0,
              boxSizing: 'border-box',
              padding: `${CARD_SHADOW_BLEED}px`,
              overflow: 'visible',
            },
          }}
        >
          <Swiper
            modules={[Autoplay]}
            onSwiper={handleSwiper}
            slidesPerView="auto"
            spaceBetween={MOBILE_SLIDE_GAP}
            loop
            loopAdditionalSlides={focusAreas.length}
            loopPreventsSliding={false}
            observer
            observeParents
            resizeObserver
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
              waitForTransition: true,
            }}
            speed={SLIDER_TRANSITION_SPEED}
            grabCursor
            breakpoints={{
              426: { spaceBetween: 24 },
              900: { spaceBetween: 32 },
              1200: { spaceBetween: SLIDE_GAP },
            }}
          >
            {slides.map((item, index) => (
              <SwiperSlide key={`${item.title}-${index}`}>
                <InitiativeCard
                  {...item}
                  sx={{
                    minHeight: { xs: 300, md: 315 },
                    '& .MuiTypography-root:first-of-type': {
                      fontSize: { xs: 24, sm: 24, md: 26, lg: 26 },
                    },
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Box>
    </Box>
  )
}
